import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class SchemaService {
    private schemaTypes: Set<string> = new Set<string>();
    private isBrowser: boolean;

    constructor(
        private router: Router,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) platformId: Object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    private clearExistingSchemas(schemaType: string): void {
        if (!this.isBrowser) return;

        try {
            const scripts = Array.from(this.document.getElementsByTagName('script'));

            const jsonLdScripts = scripts.filter(script => script.type === 'application/ld+json');

            jsonLdScripts.forEach(script => {
                try {
                    const raw = script.textContent || '';
                    const content = JSON.parse(raw || '{}');

                    if (Array.isArray(content)) {
                        if (content.some((item: any) => item && item['@type'] === schemaType)) {
                            script.parentNode?.removeChild(script);
                            return;
                        }
                    } else if (content['@type'] === schemaType) {
                        script.parentNode?.removeChild(script);
                        return;
                    }

                    if (schemaType === 'BreadcrumbList' &&
                        (content['@type'] === 'BreadcrumbList' ||
                            JSON.stringify(content).includes('BreadcrumbList'))) {
                        script.parentNode?.removeChild(script);
                        return;
                    }

                    if (schemaType === 'Organization' &&
                        (content['@type'] === 'Organization' ||
                            JSON.stringify(content).includes('Organization'))) {
                        script.parentNode?.removeChild(script);
                        return;
                    }

                    if (schemaType === 'BankOrCreditUnion' &&
                        (content['@type'] === 'BankOrCreditUnion' ||
                            JSON.stringify(content).includes('BankOrCreditUnion'))) {
                        script.parentNode?.removeChild(script);
                        return;
                    }

                } catch (error) {
                    console.error('Schema parse error:', error);
                }
            });
        } catch (error) {
            console.error('Error clearing schemas:', error);
        }
    }


    private schemaExists(schemaType: string): boolean {
        if (!this.isBrowser) return false;

        try {
            const scripts = Array.from(this.document.getElementsByTagName('script'))
                .filter(script => script.type === 'application/ld+json');

            for (const script of scripts) {
                try {
                    const content = script.textContent || '';
                    if (!content.trim()) continue;

                    const parsed = JSON.parse(content);

                    if (Array.isArray(parsed)) {
                        if (parsed.some((item: any) => item && item['@type'] === schemaType)) {
                            return true;
                        }
                    } else if (parsed && parsed['@type'] === schemaType) {
                        return true;
                    }
                } catch {
                    // ignore malformed JSON-LD
                }
            }
        } catch (error) {
            console.error('Error checking existing schemas:', error);
        }

        return false;
    }

    private addSchema(schemaType: string, schema: any): void {
        if (this.schemaTypes.has(schemaType) || this.schemaExists(schemaType)) {
            return;
        }

        this.clearExistingSchemas(schemaType);

        const script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(schema);
        this.document.head.appendChild(script);

        this.schemaTypes.add(schemaType);
    }

    addInvestmentSchemas(): void {
        if (this.router.url === '/n-kolay/yatirim/tl-doviz-repo-islemleri') {
            this.addSchema('InvestmentOrDeposit', {
                "@context": "https://schema.org",
                "@type": "InvestmentOrDeposit",
                "name": "TL/Döviz Repo İşlemleri",
                "description": "N Kolay TL ve Döviz repo işlemleri ile kısa vadeli değerlendirme imkanı. Vadeli yatırım seçenekleri ile esnek ve güvenli kazanç.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "currency": "TRY",
                "terms": "Kısa vadeli repo yatırımları. TL veya döviz cinsinden yapılabilir. Vergi avantajı mevcuttur.",
                "url": "https://www.aktifbank.com.tr/n-kolay/yatirim/tl-doviz-repo-islemleri",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }

        else if (this.router.url === '/n-kolay/yatirim/doviz-alim-satim') {
            this.addSchema('InvestmentOrDeposit', {
                "@context": "https://schema.org",
                "@type": "InvestmentOrDeposit",
                "name": "Döviz Alım-Satım",
                "description": "N Kolay mobil uygulamasıyla avantajlı kurlar üzerinden döviz alım-satımı yapabilirsiniz. USD, EUR ve daha fazlası anlık işlemle cebinizde.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "currency": "TRY",
                "terms": "Anında döviz alım-satım. Hafta içi 24 saat işlem imkanı. Uygulama üzerinden kolay erişim.",
                "url": "https://www.aktifbank.com.tr/n-kolay/yatirim/doviz-alim-satim",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }

        else if (this.router.url === '/n-kolay/yatirim/hisse-senedi') {
            this.addSchema('InvestmentOrDeposit', {
                "@context": "https://schema.org",
                "@type": "InvestmentOrDeposit",
                "name": "Hisse Senedi Yatırımı",
                "description": "N Kolay Mobil üzerinden Borsa İstanbul'da hisse senedi alım-satım işlemlerinizi kolayca gerçekleştirebilirsiniz.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "currency": "TRY",
                "terms": "BIST üzerinden hisse senedi işlemleri. Gerçek zamanlı fiyatlarla alım-satım. N Kolay uygulaması üzerinden kolay erişim.",
                "url": "https://www.aktifbank.com.tr/n-kolay/yatirim/hisse-senedi",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }

        else if (this.router.url === '/n-kolay/yatirim/aktif-portfoy-fonlari') {
            this.addSchema('InvestmentOrDeposit', {
                "@context": "https://schema.org",
                "@type": "InvestmentOrDeposit",
                "name": "Aktif Portföy Fonları",
                "description": "N Kolay Mobil üzerinden farklı risk profillerine uygun Aktif Portföy Fonları ile profesyonel yönetilen yatırım çözümlerine erişebilirsiniz.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "currency": "TRY",
                "terms": "Uzman portföy yöneticileri tarafından oluşturulan fon seçenekleri. N Kolay uygulaması üzerinden erişim.",
                "url": "https://www.aktifbank.com.tr/n-kolay/yatirim/aktif-portfoy-fonlari",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }

        else if (this.router.url === '/n-kolay/yatirim/n-kolay-bono') {
            this.addSchema('InvestmentOrDeposit', {
                "@context": "https://schema.org",
                "@type": "InvestmentOrDeposit",
                "name": "N Kolay Bono",
                "description": "N Kolay Bono, Aktif Bank tarafından sunulan, yüksek faiz getirisi sağlayan risksiz yatırım ürünüdür. Şubeye gitmeden, internet üzerinden kolayca yatırım yapabilirsiniz.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "47.50",
                "annualPercentageRate": "47.50",
                "currency": "TRY",
                "terms": "Günlük faizli, vergisiz, 1 gün vadeli ve esnek limitli bono yatırımı.",
                "url": "https://www.aktifbank.com.tr/n-kolay/yatirim/n-kolay-bono",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }

        else if (this.router.url === '/n-kolay/yatirim/n-kolay-bono-xl') {
            this.addSchema('InvestmentOrDeposit', {
                "@context": "https://schema.org",
                "@type": "InvestmentOrDeposit",
                "name": "Bono XL",
                "description": "N Kolay Bono XL, daha yüksek tutarda yatırımlar için yüksek faiz oranı sunan özel bir bono ürünüdür.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "48.00",
                "annualPercentageRate": "48.00",
                "currency": "TRY",
                "terms": "Yüksek tutarlı yatırımlar için özel oran. 1 gün vadeli, vergisiz yatırım.",
                "url": "https://www.aktifbank.com.tr/n-kolay/yatirim/n-kolay-bono-xl",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }

        else if (this.router.url === '/n-kolay/yatirim/n-kolay-fx') {
            this.addSchema('InvestmentOrDeposit', {
                "@context": "https://schema.org",
                "@type": "InvestmentOrDeposit",
                "name": "N Kolay FX",
                "description": "N Kolay FX ile döviz kurları üzerinden kazanç sağlayabileceğiniz kolay ve hızlı işlem platformu.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "currency": "USD",
                "terms": "Gerçek zamanlı döviz alım-satımı ile anlık yatırım fırsatı. Hafta içi 24 saat işlem imkanı.",
                "url": "https://www.aktifbank.com.tr/n-kolay/yatirim/n-kolay-fx",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }
    }

    addCardSchemas(): void {
        if (this.router.url === '/n-kolay/kartlar/n-kolay-kart') {
            this.addSchema('PaymentCard', {
                "@context": "https://schema.org",
                "@type": "PaymentCard",
                "name": "N Kolay Banka Kartı",
                "description": "N Kolay Banka Kartı, Türkiye genelinde kullanılabilen ön ödemeli banka kartıdır.",
                "url": "https://www.aktifbank.com.tr/n-kolay/kartlar/n-kolay-kart",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "brand": {
                    "@type": "Brand",
                    "name": "N Kolay"
                }
            });
        }
    }

    addLoanSchemas(): void {
        if (this.router.url === '/n-kolay/krediler/internetten-aninda-kredi') {
            this.addSchema('LoanOrCredit', {
                "@context": "https://schema.org",
                "@type": "LoanOrCredit",
                "name": "İnternetten Anında Kredi",
                "description": "Şubesiz, belgesiz, kefilsiz! N Kolay üzerinden online başvuruyla anında ihtiyaç kredinizi alın.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank - N Kolay",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "3.99",
                "loanType": "PersonalLoan",
                "amount": {
                    "@type": "MonetaryAmount",
                    "minValue": 1000,
                    "maxValue": 100000
                },
                "currency": "TRY",
                "termsOfLoan": {
                    "@type": "LoanOrCredit",
                    "loanTerm": {
                        "@type": "QuantitativeValue",
                        "minValue": 3,
                        "maxValue": 36,
                        "unitCode": "MON"
                    }
                },
                "url": "https://www.aktifbank.com.tr/n-kolay/krediler/internetten-aninda-kredi",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
        }

        else if (this.router.url === '/n-kolay/krediler/aninda-ihtiyac-kredisi') {
            this.addSchema('LoanOrCredit', {
                "@context": "https://schema.org",
                "@type": "LoanOrCredit",
                "name": "Aktif Bank Bireysel İhtiyaç Kredisi",
                "amount": {
                    "@type": "MonetaryAmount",
                    "currency": "TRY",
                    "value": 125000
                },
                "loanType": "İhtiyaç Kredisi",
                "loanTerm": {
                    "@type": "QuantitativeValue",
                    "value": 36,
                    "unitCode": "MON"
                },
                "interestRate": 3.69,
                "provider": {
                    "@type": "BankOrCreditUnion",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr"
                },
                "url": "https://www.aktifbank.com.tr/n-kolay/krediler/aninda-ihtiyac-kredisi"
            });
            if (!this.schemaExists('FAQPage')) {
            this.addSchema('FAQPage', {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [{
                    "@type": "Question",
                    "name": "İhtiyaç kredisi nedir?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "İhtiyaç kredisi, nakit ihtiyaçlarınızı şubeye gitmeden karşılayabileceğiniz bir kredi ürünüdür. Kredi başvurunuzu N Kolay Mobil veya internet bankacılığı üzerinden kolayca yapabilirsiniz."
                    }
                },{
                    "@type": "Question",
                    "name": "İhtiyaç kredisine kimler başvurabilir?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "18 yaşını doldurmuş, kredi notu yeterli olan, belgelenebilir gelire sahip herkes N Kolay’da ihtiyaç kredisi başvurusu yapabilir. Aktif Bank krediyi kullandırma, kampanya şartlarını ve faiz oranlarını değiştirme hakkını saklı tutar."
                    }
                },{
                    "@type": "Question",
                    "name": "İhtiyaç kredisine nasıl başvuru yapabilirim?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "N Kolay ihtiyaç kredi başvurunuzu N Kolay Mobil veya internet sitesi üzerinden kolayca yapabilirsiniz. Henüz N Kolay müşterisi değilseniz N Kolay mobili indirip dakikalar içinde müşteri olarak başvurunuzu tamamlayabilirsiniz."
                    }
                },{
                    "@type": "Question",
                    "name": "İhtiyaç kredisi başvurusu sırasında hangi belgeler gereklidir?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Başvuru anında yanınızda Türkiye Cumhuriyeti kimlik kartını bulundurmanız yeterlidir."
                    }
                },{
                    "@type": "Question",
                    "name": "İhtiyaç kredisi başvurum ne kadar sürede sonuçlanır?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "N Kolay’da ihtiyaç kredisi başvurusu dakikalar içinde sonuçlanır. Hızlı onay süreci sayesinde beklemek zorunda kalmadan finansman ihtiyacınızı karşılayabilirsiniz."
                    }
                },{
                    "@type": "Question",
                    "name": "Onaylanan kredi ne zaman hesabıma geçer?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Kredi başvurunuz sonrasında onaylanan tutar dakikalar içinde hesabınıza yatırılır. N Kolay Mobil uygulamasından bakiyenizi kontrol edebilir, ödemelerinizi hemen yapabilirsiniz."
                    }
                },{
                    "@type": "Question",
                    "name": "İhtiyaç kredisi faiz oranları nasıl hesaplanır?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Kredi faiz oranları belirlenirken banka değerlendirme kriterleri ve müşteri risk profili baz alınır."
                    }
                },{
                    "@type": "Question",
                    "name": "En az/en fazla ne kadar kredi kullanabilirim?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "En az 2.000 TL, en fazla 500.000 TL kredi kullanabilirsiniz."
                    }
                },{
                    "@type": "Question",
                    "name": "İhtiyaç kredisi için maksimum vade süresi nedir?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "En fazla 36 ay vadeli kredi kullanabilirsiniz. 125.000 TL'ye kadar başvurularınız için 36 ay, 125.001 TL ile 250.000 TL arasındaki başvurularınız için 24 ay, 250.001 TL ve üzerindeki başvurularınız içinse maksimum 12 ay vade olacak şekilde kredi başvurusunda bulunabilirsiniz."
                    }
                },{
                    "@type": "Question",
                    "name": "Kredi taksitlerini nerelerden ödeyebilirim?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "• N Kolay mobilden, • İnternet şubemizden, • Herhangi bir bankada bulunan hesabınızdan, bankamızdaki IBAN'ınıza EFT/FAST yaparak veya N Kolay banka kartınıza para yükleyerek, • Aktif Bank şubelerinden, • Tüm PTT şubelerinden ve PttMatikIerden, • Vakıfbank ATM'lerinden."
                    }
                },{
                    "@type": "Question",
                    "name": "İhtiyaç kredisi kullanımında tahsis ve kredi kullandırım ücreti alınıyor mu?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "N Kolay’dan kullandırılan kredi tutarının binde beşi oranında kredi tahsis ücreti alınır, kullandırım ücreti alınmaz."
                    }
                },{
                    "@type": "Question",
                    "name": "Başvuruda kefil isteniyor mu?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Hayır, kredi başvurularında kefil istenmez."
                    }
                }]
            });
            }
        }

        else if (this.router.url === '/n-kolay/krediler/tasit-kredisi') {
            this.addSchema('LoanOrCredit', {
                "@context": "https://schema.org",
                "@type": "LoanOrCredit",
                "name": "Taşıt Kredisi",
                "description": "Yeni veya ikinci el araç alımlarınızda N Kolay Taşıt Kredisi ile avantajlı faiz oranları ve esnek vadelerden yararlanın.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank - N Kolay",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "3.89",
                "loanType": "AutoLoan",
                "amount": {
                    "@type": "MonetaryAmount",
                    "minValue": 10000,
                    "maxValue": 400000
                },
                "currency": "TRY",
                "termsOfLoan": {
                    "@type": "LoanOrCredit",
                    "loanTerm": {
                        "@type": "QuantitativeValue",
                        "minValue": 3,
                        "maxValue": 48,
                        "unitCode": "MON"
                    }
                },
                "url": "https://www.aktifbank.com.tr/n-kolay/krediler/tasit-kredisi",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
            if (!this.schemaExists('FAQPage')) {
                this.addSchema('FAQPage', {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [{
                        "@type": "Question",
                        "name": "N Kolay Taşıt kredi başvurusu için gerekli belgeler nelerdir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Taşıt kredisi başvurusu için kimliğiniz (Nüfus Cüzdanı) yeterlidir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "N Kolay Taşıt kredisi başvurusu nasıl yapılır?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yeni bir araç almak veya mevcut aracınızı yenilemek için size en yakın anlaşmalı bayilerimizden başvuru yapabilirsiniz. Aktif Bank, gerekli gördüğü durumlarda başvuruları reddetme, ek bilgi veya belge isteme, kredi koşullarını değiştirme veya kampanyayı durdurma hakkına sahiptir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "En fazla kaç ay vade ile taşıt kredisi kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "48 aya varan vade seçenekleri ile taşıt kredisi başvurusu yapılabilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "2. el araç satışıyla kaç yıla kadar araç alınabilir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Anlaşmalı bayilerimizden 14 yaşını geçmemiş istediğiniz marka ve modeldeki aracı kredilendirebilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "N Kolay Taşıt kredisi alınırken rehin zorunlu mu?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet. Taşıt kredisi kullanımında satın alınan araca rehin konulmaktadır. Rehin işlemleri için Türkiye Noterler Birliği'ne ödenmek üzere 2025 yılı için geçerli olan 279,64 TL ücret alınmaktadır. Ücret her yıl yeniden değerlendirme oranına göre değişebilir. Rehin işlemleri Aktif Yatırım Bankası tarafından otomatik yürütülür."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksit ödeme kanalları nelerdir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Taksit ödemelerinizi N Kolay Mobil, Aktif Bank İnternet Şube, tüm PTT işyerleri, PTTMatikler, VakıfBank ATM'leri üzerinden veya Aktif Bank hesabınıza EFT yaparak gerçekleştirebilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "En az/en fazla ne kadar taşıt kredisi kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "BDDK kararına göre taşıt kredisi vade ve kredilendirme oranları şöyledir: 400.000 TL altı araçlar için %70'e kadar ve maksimum 36 ay vade; 400.001 TL–800.000 TL arası araçlar için %50'ye kadar ve maksimum 36 ay vade; 800.001 TL–1.200.000 TL arası araçlar için %30'a kadar ve maksimum 24 ay vade; 1.200.001 TL–2.000.000 TL arası araçlar için %20'ye kadar ve maksimum 12 ay vade uygulanır. 2.000.000 TL üzerindeki araçlar için kredi kullandırılamaz."
                        }
                    }]
                });
            }
        }

        else if (this.router.url === '/n-kolay/krediler/taksitli-alisveris-kredisi') {
            this.addSchema('LoanOrCredit', {
                "@context": "https://schema.org",
                "@type": "LoanOrCredit",
                "name": "Taksitli Alışveriş Kredisi",
                "description": "Mağaza ya da online alışverişlerinizde N Kolay ile anında kredi, kolay ödeme seçenekleriyle taksitle alışveriş.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank - N Kolay",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "3.75",
                "loanType": "RetailInstallmentLoan",
                "amount": {
                    "@type": "MonetaryAmount",
                    "minValue": 500,
                    "maxValue": 30000
                },
                "currency": "TRY",
                "termsOfLoan": {
                    "@type": "LoanOrCredit",
                    "loanTerm": {
                        "@type": "QuantitativeValue",
                        "minValue": 3,
                        "maxValue": 12,
                        "unitCode": "MON"
                    }
                },
                "url": "https://www.aktifbank.com.tr/n-kolay/krediler/taksitli-alisveris-kredisi",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
            if (!this.schemaExists('FAQPage')) {
                this.addSchema('FAQPage', {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [{
                        "@type": "Question",
                        "name": "N Kolay Taksit Alışveriş Kredisi'ne nasıl başvurabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "nkolay.com web sitemiz üzerinden N Kolay Taksit Alışveriş Kredisi sayfasındaki \"Hemen Başvur\" butonuna tıklayarak veya anlaşmalı mağazalarımıza giderek mağazanın size özel oluşturacağı başvuru QR kodu ile başvurabilirsiniz. Ayrıca, nkolay.com web sitemiz üzerinden size en yakın anlaşmalı mağazayı ve satın almak istediğiniz ürün türünü seçerek dijital başvurunuzu oluşturabilir, sözleşmeleri onaylayıp mağazayı ziyaret ederek kredinizi kullanabilirsiniz. Kredi tutarı, satın alınan ürün karşılığında yasal kesintiler sonrası anlaşmalı mağazanın hesabına aktarılacaktır. Başvuruyu web sitesinde oluşturduktan sonra seçtiğiniz anlaşmalı mağazaya giderek işlemleri tamamlayıp ürününüzü teslim alabilirsiniz. Aktif Bank, uygun görmediği kredi başvurularını reddetme ve gerekli durumlarda ek bilgi veya belge isteme, kredi koşullarını değiştirme, kampanyayı durdurma veya iptal etme hakkına sahiptir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredi başvurunuz için gereken belgeler nelerdir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Başvuru için nüfus cüzdanınızla işlem yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Koruma Kredisi nedir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Başvurunuzda talep etmiş olduğunuz diğer ürünlerin ödemesi yapılmak üzere alışveriş kredi tutarına eklenir. Kredinizi kullanmak için mağazayı ziyaret ettiğinizde diğer ürünlere ilişkin anlaşmalı mağazadan destek alabilirsiniz. Detaylı bilgi: sigortayeri.com/k-koruma-sigortasi"
                        }
                    }, {
                        "@type": "Question",
                        "name": "N Kolay Taksit Alışveriş Kredisi'nde tahsis ücreti veya kredi kullandırım ücreti alınıyor mu?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredi Tahsis Ücreti, kredi tutarının %0.5'i (BSMV hariç) kadardır."
                        }
                    }, {
                        "@type": "Question",
                        "name": "En az/en fazla kaç ay vade ile N Kolay Taksit Alışveriş Kredisi kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "3–36 aya varan vade seçenekleri ile N Kolay Taksit Alışveriş Kredisi kullanabilirsiniz. Cep telefonu alımlarında vade; 20.000 TL'ye kadar 12 ay, 20.000 TL ve üzeri için 3 ay olarak sınırlandırılmıştır. Bilgisayar alımlarında vade 12 ay, tablet alımlarında 6 ay ile sınırlıdır."
                        }
                    }, {
                        "@type": "Question",
                        "name": "En az/en fazla ne kadar N Kolay Taksit Alışveriş Kredisi kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Anlaşmalı beyaz eşya ve mobilya mağazalarımızdan 1.000 TL ile 100.000 TL arasında kredi kullanabilirsiniz. Bireysel ihtiyaç kredilerinde; kalan ana para bakiyesi toplamı 125.000 TL'ye kadar olan başvurular için 36 ay, 125.000–250.000 TL arasındaki başvurular için 24 ay, 250.001 TL ve üzeri başvurular için maksimum 12 ay vadedir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredi taksit ödemelerimi nasıl yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Taksit ödemelerinizi; N Kolay mobil uygulamasında İşlemler > Krediler > Taksit Ödemesi menüsünden, Aktif Bank internet şubesinde Krediler > Taksit Ödeme menüsünden yapabilirsiniz. Ayrıca tüm PTT işyerlerinden TC kimlik numaranız, başvuru veya müşteri numaranız ile 12.50 TL karşılığında ödeme yapabilirsiniz. PTT Matiklerden TC kimlik numaranız ile Aktif Bank IBAN'ınıza ücretsiz para yatırabilirsiniz. Diğer bankalardan IBAN'a EFT yapabilir, Vakıfbank ATM'lerinden %0,8 komisyonla kartsız işlemle para yatırabilirsiniz. Anlaşmalı UPT bayilerinden nüfus cüzdanınızla ücretsiz para yatırabilirsiniz."
                        }
                    }]
                });
            }
        }

        else if (this.router.url === '/n-kolay/krediler/kredili-destek-hesabi') {
            this.addSchema('LoanOrCredit', {
                "@context": "https://schema.org",
                "@type": "LoanOrCredit",
                "name": "Kredili Destek Hesabı",
                "description": "Hesabınızda para olmasa da harcama yapabilmenizi sağlayan Kredili Destek Hesabı ile ihtiyaç anında cebinizde nakit!",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank - N Kolay",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "4.25",
                "loanType": "Overdraft",
                "amount": {
                    "@type": "MonetaryAmount",
                    "minValue": 500,
                    "maxValue": 10000
                },
                "currency": "TRY",
                "termsOfLoan": {
                    "@type": "LoanOrCredit",
                    "loanTerm": {
                        "@type": "QuantitativeValue",
                        "minValue": 1,
                        "unitCode": "MON"
                    }
                },
                "url": "https://www.aktifbank.com.tr/n-kolay/krediler/kredili-destek-hesabi",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
            if (!this.schemaExists('FAQPage')) {
                this.addSchema('FAQPage', {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [{
                        "@type": "Question",
                        "name": "Kredili Destek Hesabı faizi nedir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredili Destek Hesabı aylık faiz oranı %4,50'dir (vergiler hariç)."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredili Destek Hesabı borcumu ne zaman ödemeliyim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredili Destek Hesabı limitinizi kullandığınızda hesap bakiyeniz ekside görünür ve her gün günlük faiz işler. Ayın son iş günü, o aya ait faiz hesaplanarak hesabınıza borç olarak yansır ve ekstrenize yansıdığı andan itibaren 30 gün içinde ödemeniz gerekir. Aynı gün kullanıp kapatırsanız 1 günlük faiz uygulanır. Hafta sonu veya tatil günlerinde aynı gün kullanım ve kapatma yapılırsa bir sonraki iş gününe kadar faiz işler. Kredili Destek Hesabınızın bağlı olduğu hesabınıza yeterli para girişi olursa borcunuz otomatik kapanır. Limiti kullanmadıkça faiz veya ücret ödemezsiniz. Kullanılan anaparanın tamamı en geç 360 gün içinde ödenmelidir. Anapara gecikme faizi %4,80'dir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredili Destek Hesabı faiz borcumu nasıl görüntüleyebilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Ay sonlarında ekstreye yansıyan Kredili Destek Hesabı faiz borcunu İnternet Şubesi hesap hareketlerinden veya e-posta ile gönderilen hesap ekstresinden görüntüleyebilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredili Destek Hesabı kullanımına ilişkin borcumu nasıl öderim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Borcunuzu Aktif Bank İnternet Şube'den, Aktif Bank şubelerinden, PTT şubeleri ve PTTMatik'lerden, VakıfBank ATM'lerinden, başka bankalardaki hesabınızdan Aktif Bank hesabınıza EFT yaparak veya N Kolay Banka Kartı ile diğer banka ATM'lerinden para yatırarak ödeyebilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Pttmatik'ten nasıl ödeme yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Pttmatik'te Anlaşmalı Banka İşlemleri > Aktif Bank İşlemleri > Hesaba Nakit Yatırma adımlarını izleyin. Kart numarası veya hesap numarası seçin, bilgiler ve cep telefonu numarası girildikten sonra işlem ücretini onaylayarak ödeme yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "VakıfBank ATM'sinden nasıl ödeme yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kartsız İşlemler > Aktif Bank İşlemleri menüsüne girerek kart numarası veya hesap numarası girilir. TC kimlik numarası ve cep telefonu numarası yazıldıktan sonra işlem ücreti onaylanarak ödeme yapılabilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredili Destek Hesabı limiti neye göre belirlenir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredili Destek Hesabı limiti Aktif Bank politikalarına göre belirlenmektedir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksitli Kredili Destek Hesabı nedir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredili Destek Hesabı limitinin %80'ine kadar olan kısmının nakit olarak taksitli şekilde kullanılabilmesidir. Kullanabilmek için aktif bir Kredili Destek Hesabı olması gerekir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksitli Kredili Destek Hesabını nasıl kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "N Kolay Mobil üzerinden Hesaplar > Kredili Destek Hesabı > KDH Taksit İşlemleri > Taksitli KDH Kullanımı adımlarını izleyerek kullanabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Birden fazla Taksitli Kredili Destek Hesabı kullanabilir miyim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet, kullanılabilir Taksitli KDH limitiniz dahilinde birden fazla kullanım yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "En az/en fazla ne kadar Taksitli Kredili Destek Hesabı kullanımı yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "En az 500 TL, en fazla KDH limitinizin %80'i kadar Taksitli KDH kullanabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksitli Kredili Destek Hesap faizi nedir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Aylık faiz oranı %4,50'dir (vergiler hariç)."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksitlendirme süresi kaç aydır?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Taksitlendirme en az 3 ay, en fazla 12 ay olabilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksitli KDH'ı nerelerde kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Alışveriş ve fatura ödemelerinde kullanabilir, nakit çekebilir veya EFT/havale yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksit ödemelerimi nasıl yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Taksit ödemelerinizi N Kolay Mobil üzerinden yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksitli KDH kullandığımda ilk taksit tarihim ne zamandır?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Ödeme planı her ayın 1'ine göre oluşturulur. Örneğin 19 Haziran'da kullanım yapıldığında ilk taksit 1 Ağustos olur. Taksit günü daha sonra değiştirilebilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Taksit tutarım limitimden düşer mi? Ödediğimde limitime geri eklenir mi?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet. Taksitli KDH kullandığınız kadar limitiniz azalır, ödeme yaptıkça limitiniz yeniden kullanılabilir hâle gelir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredili Destek Hesabımla yaptığım harcamaları sonradan taksitlendirebilir miyim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet. KDH limitinizden yapılan ve taksitlendirmeye uygun olan alışveriş ve harcamalar sonradan taksitlendirilebilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredili Destek Hesabımla yaptığım hangi harcamaları sonradan taksitlendirebilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "EFT, FAST, fatura ödeme işlemleri ile N Kolay banka kartı üzerinden yapılan taksitlendirmeye uygun alışverişleriniz taksitlendirilebilir. Ayrıca KDH limitinden yapılan nakit çekim işlemleri de taksitlendirme kapsamındadır."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Alışveriş/harcama taksitlendirme işleminde bir limit sınırı var mı?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Harcamanızın tamamını KDH ile ödediyseniz, kullanılabilir taksitli KDH limitiniz dahilinde taksitlendirme yapılabilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "KDH limitimin ne kadarını taksitli kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "KDH limitinizin %80'ine kadar taksitli KDH kullanabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "KDH limitimi nasıl artırabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "N Kolay Mobil üzerinden Hesaplar > Kredili Destek Hesabı > KDH Limit Arttırımı adımlarını izleyerek limit artırımı talebinde bulunabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "KDH harcamamı veya limitimi nasıl taksitlendirebilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Taksitlendirme işlemlerini N Kolay Mobil üzerinden Hesaplar > Kredili Destek Hesabı > KDH Taksit İşlemleri menüsünden yapabilir veya 0850 724 77 77 çağrı merkezinden destek alabilirsiniz."
                        }
                    }]
                });
            }
        }

        else if (this.router.url === '/n-kolay/krediler/bes-teminatli-kredi-ile-geleceginizi-guvence-altinda-tutun') {
            this.addSchema('LoanOrCredit', {
                "@context": "https://schema.org",
                "@type": "LoanOrCredit",
                "name": "BES Teminatlı Kredi",
                "description": "Bireysel Emeklilik Sistemi birikiminizi teminat göstererek daha avantajlı faiz oranlarıyla kredi alın.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank - N Kolay",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "3.49",
                "loanType": "SecuredLoan",
                "amount": {
                    "@type": "MonetaryAmount",
                    "minValue": 5000,
                    "maxValue": 150000
                },
                "currency": "TRY",
                "termsOfLoan": {
                    "@type": "LoanOrCredit",
                    "loanTerm": {
                        "@type": "QuantitativeValue",
                        "minValue": 3,
                        "maxValue": 36,
                        "unitCode": "MON"
                    }
                },
                "url": "https://www.aktifbank.com.tr/n-kolay/krediler/bes-teminatli-kredi-ile-geleceginizi-guvence-altinda-tutun",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
            if (!this.schemaExists('FAQPage')) {
                this.addSchema('FAQPage', {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [{
                        "@type": "Question",
                        "name": "BES Teminatlı krediye nasıl başvuru yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Bireysel Emeklilik Sisteminde (BES) birikiminiz bulunuyorsa N Kolay Mobil uygulaması, nkolay.com veya aktifbank.com.tr üzerinden başvurabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredi başvurusu için gerekli belgeler nelerdir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "BES Teminatlı kredi başvurusu için nüfus cüzdanınız yeterlidir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı krediyi en az/en fazla kaç ay vade ile kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "En az 3, en fazla 36 ay vadeli olarak kullanabilirsiniz. 125.000 TL'ye kadar 36 ay, 125.001 TL–250.000 TL arası başvurular için 24 ay, 250.000 TL üzeri başvurular için maksimum 12 ay vade uygulanır. Ayrıca kredi vadesi mevcut BES sözleşmenizin vadesini geçemez."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı krediyi en az/en fazla ne kadar kullanabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "En az 2.000 TL, en fazla 500.000 TL kredi kullanabilirsiniz. Temlik altına alınacak BES birikiminizin, talep edilen kredi tutarının en az 1,2 katı olması gerekmektedir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı krediye başvurduktan sonra kredi tutarım ne zaman hesabıma geçer?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Başvurunuz tamamlanıp, Emeklilik Gözetim Merkezi tarafından BES birikiminizin temlik altına alındığı bilgisi iletildiğinde, kredi tutarınız anında hesabınıza geçer."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı kredinin avantajları nelerdir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "BES Teminatlı kredi ile BES birikiminiz değerlenmeye devam eder. Size özel faiz oranı ile şubeye gitmeden, evrak işleri olmadan dijital kanallar üzerinden başvuru yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES birikimimdeki teminat boyunca fonum değerlendirilmeye devam edecek midir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet. Teminat altındaki tutar ayrı bir fona aktarılır ve bu fonda değerlendirilmeye devam eder."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı kredide sigorta zorunlu mu?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Sigorta zorunlu değildir. Dilerseniz sigortalı kredi seçebilir ve daha düşük faiz oranlarından yararlanabilirsiniz. Sigorta primi kredi tutarı, vadesi ve yaşa göre değişebilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı krediye kimler başvuru yapabilir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "T.C. vatandaşı olan, 18 yaşından büyük ve BES birikimi bulunan herkes başvuruda bulunabilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı kredide tahsis ücreti veya kullandırım ücreti alınıyor mu?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredi tahsis ücreti, kredi tutarının %0,5'i kadardır (BSMV hariç). Kullandırım ücreti alınmaz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı krediyi kapattığımda birikimlerim üzerindeki teminat ne zaman kaldırılır?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredi kapanınca birikimleriniz otomatik olarak eski fonlarına aktarılır. Teminat süreci fon türüne göre yaklaşık 10 gün sürebilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "BES Teminatlı kredide cayma veya erken kapama yapabilir miyim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Krediyi kullandıktan sonra 14 gün içinde cayma hakkınızı kullanabilirsiniz. Erken kapama için kapama yapılacak günün borç tutarını N Kolay hesabınıza yatırmanız gerekir. Cayma ve kapama işlemleri için 0850 724 30 50 Aktif Bank İletişim Merkezi aranabilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Krediyi kullandıktan sonra ödemelerimi nasıl yaparım?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Ödemelerinizi N Kolay Mobil, Aktif Bank İnternet Şube, Aktif Bank şubeleri, tüm PTT şubeleri ve PttMatik'ler, VakıfBank ATM'leri, başka bankalardan EFT veya N Kolay Banka Kartı ile diğer banka ATM'lerinden yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "VakıfBank ATM'sinden nasıl ödeme yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kartsız İşlemler > Aktif Bank İşlemleri menüsüne girip kart veya hesap numarası girilir. TC kimlik ve telefon numarası yazılır. Bilgiler onaylandıktan sonra işlem ücreti kabul edilerek ödeme yapılabilir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Pttmatik'lerden nasıl ödeme yapabilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Pttmatik'te Anlaşmalı Banka İşlemleri > Aktif Bank İşlemleri > Hesaba Nakit Yatırma menüsünden kart veya hesap numarasını seçerek gerekli bilgileri girip ödeme yapabilirsiniz."
                        }
                    }]
                });
            }
        }

        else if (this.router.url === '/n-kolay/krediler/kolay-nakit') {
            this.addSchema('LoanOrCredit', {
                "@context": "https://schema.org",
                "@type": "LoanOrCredit",
                "name": "Kolay Nakit",
                "description": "Cebinizde anında nakit! Dilediğiniz zaman kullanabileceğiniz, anında hesabınıza geçen Kolay Nakit kredi imkanı.",
                "provider": {
                    "@type": "FinancialService",
                    "name": "Aktif Bank - N Kolay",
                    "url": "https://www.aktifbank.com.tr",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg"
                    }
                },
                "interestRate": "3.95",
                "loanType": "LineOfCredit",
                "amount": {
                    "@type": "MonetaryAmount",
                    "minValue": 1000,
                    "maxValue": 10000
                },
                "currency": "TRY",
                "termsOfLoan": {
                    "@type": "LoanOrCredit",
                    "loanTerm": {
                        "@type": "QuantitativeValue",
                        "minValue": 1,
                        "maxValue": 6,
                        "unitCode": "MON"
                    }
                },
                "url": "https://www.aktifbank.com.tr/n-kolay/krediler/kolay-nakit",
                "areaServed": {
                    "@type": "Country",
                    "name": "TR"
                }
            });
            if (!this.schemaExists('FAQPage')) {
                this.addSchema('FAQPage', {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [{
                        "@type": "Question",
                        "name": "Kolay Nakit nedir?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kolay Nakit; standart tüketici kredisine göre daha küçük tutarlarda, tek taksitli ve müşterilerin ödemelerinde veya POS harcamalarında kullanabileceği, anında ihtiyaç karşılamaya yönelik belirli süreli bir tüketici kredisidir. Kolay Nakit başvurunuz, müşteri risk profiliniz ve banka politikalarına göre değerlendirilir ve üst limiti 5.000 TL'dir."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kolay Nakit'i nasıl kullanırım?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kolay Nakit'e N Kolay Mobil uygulaması veya nkolay.com web sitesi üzerinden başvurabilirsiniz. Aktif Bank müşterisiyseniz başvurunuz onaylandığında paranız anında hesabınıza geçer. Aktif Bank müşterisi değilseniz N Kolay Mobil üzerinden görüntülü görüşme ile müşterimiz olabilir, başvurunuzu tamamladıktan sonra paranızı anında kullanabilirsiniz. Görüntülü görüşme için çipli T.C. Kimlik Kartınızın yanınızda olması ve telefonunuzun NFC özelliğinin açık olması gerekmektedir. Başvurunuzun tamamlanması için sözleşmelerinizi N Kolay Mobil üzerinden onaylamanız gerekir. Sözleşme onayından sonra paranız anında hesabınıza geçer. Kolay Nakit her 30 günde en fazla 2 kez kullanılabilir; haklarınız ilk kullanımınızdan 30 gün sonra yenilenir. Kolay Nakit'i kullandıktan sonra belirlenen faizsiz sürenin bitiminde yapılacak ödemelerde, kullanım tarihinden itibaren geçen gün sayısı üzerinden faiz işletilir. Kolay Nakit limiti aynı anda yalnızca 1 kez kullanılabilir; yeni kullanım için mevcut Kolay Nakit'in tamamen ödenmiş olması gerekir."
                        }
                    }]
                });
            }
        }

        else if (this.router.url === '/n-kolay/krediler/kredi-hesaplama') {
            if (!this.schemaExists('FAQPage')) {
                this.addSchema('FAQPage', {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [{
                        "@type": "Question",
                        "name": "Aylık kredi taksit tutarını nasıl öğrenebilirim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Aylık taksit tutarını öğrenmek için N Kolay'ın kredi hesaplama aracını kullanabilirsiniz. Kredi tutarı ve vade süresini girerek aylık ödeme planınızı hemen görebilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "N Kolay Kredi faiz oranları nasıl hesaplanır?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "N Kolay Kredi faiz oranları belirlenirken banka değerlendirme kriterleri ve müşteri risk profili dikkate alınır."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Hesaplama sonucuna göre direkt başvuru yapabilir miyim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet. Kredi hesaplama işlemini tamamladıktan sonra aylık taksit tutarınızı gördüğünüz ekrandaki 'Paranı Hemen Al' adımını takip ederek doğrudan başvuru yapabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredi hesaplaması yaparken ek masraflar dahil mi?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet. Kredi hesaplama aracındaki sonuçlara banka kredi faiz oranlarıyla birlikte BSMV ve KKDF gibi yasal masraflar da dahildir. Böylece toplam geri ödeme tutarını sürpriz ücretler olmadan net olarak görebilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredi hesaplaması kredi notumu etkiler mi?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Hayır. Kredi hesaplama işlemi bilgi amaçlıdır ve herhangi bir kayıt oluşturulmadığı için kredi notunuzu etkilemez."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredi hesaplaması yaptıktan sonra kararımı değiştirebilir miyim?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Evet. Hesaplama yaptıktan sonra dilediğiniz zaman farklı tutar ve vade seçeneklerini yeniden değerlendirebilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Kredi başvurum ne kadar sürede sonuçlanır?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "N Kolay kredi başvuruları dakikalar içinde sonuçlanır. Hızlı onay süreci sayesinde beklemeden finansman ihtiyacınızı karşılayabilirsiniz."
                        }
                    }, {
                        "@type": "Question",
                        "name": "Onaylanan kredi ne zaman hesabıma geçer?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Kredi başvurunuz onaylandığında tutar dakikalar içinde hesabınıza yatırılır. N Kolay Mobil uygulamasından bakiyenizi kontrol ederek ödemelerinizi hemen yapabilirsiniz."
                        }
                    }]
                });
            }
        }
    }

    addOrganizationSchema(): void {
        if (this.router.url === '/hakkimizda/hizmet-noktalarimiz/subelerimiz') {
            this.addSchema('Organization', {
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "Aktif Bank",
                "url": "https://www.aktifbank.com.tr",
                "location": [
                    {
                        "@type": "Place",
                        "name": "Adana Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Kurtuluş Mah. Ziyapaşa Bulvarı Lütfiye Hanım Apt. 39/1",
                            "addressLocality": "Seyhan",
                            "addressRegion": "Adana",
                            "postalCode": "01000",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-322-503-05-63",
                        "faxNumber": "+90-212-340-88-09"
                    },
                    {
                        "@type": "Place",
                        "name": "Ankara Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Filistin Caddesi No:2/A Gaziosmanpaşa",
                            "addressLocality": "Çankaya",
                            "addressRegion": "Ankara",
                            "postalCode": "06700",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-312-286-44-02",
                        "faxNumber": "+90-212-376-31-22"
                    },
                    {
                        "@type": "Place",
                        "name": "Antalya Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Nazlı İş Merkezi Çağlayan Mah. Barınaklar Blv. No:56/A",
                            "addressLocality": "Muratpaşa",
                            "addressRegion": "Antalya",
                            "postalCode": "07230",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-242-323-60-75",
                        "faxNumber": "+90-212-340-88-09"
                    },
                    , {
                        "@type": "Place",
                        "name": "İstanbul Anadolu Özel Bankacılık Şubesi",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Bağdat Cad. Caddebostan Mh. Yayla İş Merkezi No:338 Kat:3 D:5",
                            "addressLocality": "Kadıköy",
                            "addressRegion": "İstanbul",
                            "postalCode": "34728",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-216-467-52-08",
                        "faxNumber": "+90-212-376-31-42"
                    }, {
                        "@type": "Place",
                        "name": "İstanbul Anadolu Kurumsal Şubesi",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Sarıkanarya Sk. No:18 Yolbulan Plaza B Blok K:7 Kozyatağı",
                            "addressLocality": "Kadıköy",
                            "addressRegion": "İstanbul",
                            "postalCode": "34742",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-216-384-54-56"
                    }, {
                        "@type": "Place",
                        "name": "Başkent Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Koç Kuleleri Söğütözü Mah. Söğütözü Cad. No:2 A Blok Kat:24 Ofis No:67-68",
                            "addressLocality": "Çankaya",
                            "addressRegion": "Ankara",
                            "postalCode": "06510",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-312-512-48-48",
                        "faxNumber": "+90-212-376-31-33"
                    }, {
                        "@type": "Place",
                        "name": "Bodrum Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Çırkan Mahallesi, Atatürk Bulvarı No:23/D1",
                            "addressLocality": "Bodrum",
                            "addressRegion": "Muğla",
                            "postalCode": "48400",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-252-319-78-99",
                        "faxNumber": "+90-212-340-85-02"
                    }, {
                        "@type": "Place",
                        "name": "Bursa Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Odunluk Mahallesi Akpınar Cad. No: 21 R Plaza",
                            "addressLocality": "Nilüfer",
                            "addressRegion": "Bursa",
                            "postalCode": "16110",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-224-242-62-72",
                        "faxNumber": "+90-212-376-31-34"
                    }, {
                        "@type": "Place",
                        "name": "Gaziantep Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "İncili Pınar Mahallesi Muammer Aksoy Bulvarı No:30/C",
                            "addressLocality": "Şehitkamil",
                            "addressRegion": "Gaziantep",
                            "postalCode": "27090",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-342-324-79-00",
                        "faxNumber": "+90-212-376-31-21"
                    }, {
                        "@type": "Place",
                        "name": "Marmara Kurumsal Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Yeşilbağlar Mah. Kaptan Sok. Metro Ofis Plaza No:19 İç Kapı No:16 Kat 5",
                            "addressLocality": "Pendik",
                            "addressRegion": "İstanbul",
                            "postalCode": "34893",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-216-987-31-17",
                        "faxNumber": "+90-212-340-80-84"
                    }, {
                        "@type": "Place",
                        "name": "Zincirlikuyu Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Büyükdere Cad. No:163/A Zincirlikuyu",
                            "addressLocality": "Şişli",
                            "addressRegion": "İstanbul",
                            "postalCode": "34394",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-212-340-80-40",
                        "faxNumber": "+90-212-340-80-86"
                    }, {
                        "@type": "Place",
                        "name": "İzmir Özel Bankacılık Şubesi",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Akdeniz Mah., Cumhuriyet Bul., No: 97/ A",
                            "addressLocality": "Konak",
                            "addressRegion": "İzmir",
                            "postalCode": "35210",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-232-421-15-90",
                        "faxNumber": "+90-212-376-31-20"
                    }, {
                        "@type": "Place",
                        "name": "Kayseri Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Cumhuriyet Mahallesi Millet Caddesi No:26",
                            "addressLocality": "Melikgazi",
                            "addressRegion": "Kayseri",
                            "postalCode": "38030",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-352-336-84-00",
                        "faxNumber": "+90-212-376-31-32"
                    },
                    {
                        "@type": "Place",
                        "name": "İstanbul Avrupa Kurumsal Şubesi",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Yeşilyurt Mah. Sipahioğlu Cad. No:17 B/1",
                            "addressLocality": "Bakırköy",
                            "addressRegion": "İstanbul",
                            "postalCode": "34149",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-212-376-33-01",
                        "faxNumber": "+90-212-340-88-19"
                    },
                    {
                        "@type": "Place",
                        "name": "İzmir Ege Kurumsal Şubesi",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Çınarlı Mahallesi, Şehit Polis Fethi Sekin Caddesi, No:3 Sunucu Plaza B Blok K:14",
                            "addressLocality": "Bayraklı",
                            "addressRegion": "İzmir",
                            "postalCode": "35090",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-232-434-06-84",
                        "faxNumber": "+90-212-340-84-83"
                    },
                    {
                        "@type": "Place",
                        "name": "Nuruosmaniye Şube",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Molla Fenari Mah. Türbedar Sok. No:9/2",
                            "addressLocality": "Fatih",
                            "addressRegion": "İstanbul",
                            "postalCode": "34120",
                            "addressCountry": "TR"
                        },
                        "telephone": "+90-212-987-19-87",
                        "faxNumber": "+90-212-340-84-89"
                    }
                ]
            });
        }
    }

    addBreadcrumbSchema(): void {
        if (!this.isBrowser) return;

        const gtmScript = this.document.getElementById('gtm-script');
        if (gtmScript) {
            return;
        }

        const path = this.router.url;
        const segments = path.split('/').filter(segment => segment);

        const items = [];
        let currentPath = '';

        items.push({
            "@type": "ListItem",
            "position": 1,
            "name": "Anasayfa",
            "item": "https://www.aktifbank.com.tr"
        });

        segments.forEach((segment, index) => {
            currentPath += `/${segment}`;

            let name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

            if (segment === 'n-kolay') name = 'N Kolay';
            if (segment === 'krediler') name = 'Krediler';
            if (segment === 'kartlar') name = 'Kartlar';
            if (segment === 'yatirim') name = 'Yatırım';
            if (segment === 'internetten-aninda-kredi') name = 'İnternetten Anında Kredi';
            if (segment === 'tasit-kredisi') name = 'Taşıt Kredisi';
            if (segment === 'taksitli-alisveris-kredisi') name = 'Taksitli Alışveriş Kredisi';
            if (segment === 'n-kolay-kart') name = 'N Kolay Kart';

            items.push({
                "@type": "ListItem",
                "position": index + 2,
                "name": name,
                "item": `https://www.aktifbank.com.tr${currentPath}`
            });
        });

        const script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'gtm-script';
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": items
        });

        this.clearExistingSchemas('BreadcrumbList');

        this.document.head.appendChild(script);

        this.schemaTypes.add('BreadcrumbList');
    }

    addLoanCollectionSchema(): void {
        if (this.router.url === '/n-kolay/krediler') {
            this.clearExistingSchemas('BankOrCreditUnion');
            
            this.addSchema('FinancialProduct', {
                "@context": "https://schema.org",
                "@type": "FinancialProduct",
                "name": "N Kolay Krediler",
                "description": "Aktif Bank N Kolay çatısı altında sunulan kredi ürünleriyle bireysel finansal ihtiyaçlarınıza pratik çözümler sunar.",
                "provider": {
                    "@type": "BankOrCreditUnion",
                    "name": "Aktif Bank",
                    "url": "https://www.aktifbank.com.tr"
                },
                "hasOfferCatalog": {
                    "@type": "OfferCatalog",
                    "name": "N Kolay Kredi Ürünleri",
                    "itemListElement": [
                        {
                            "@type": "Offer",
                            "url": "https://www.aktifbank.com.tr/n-kolay/krediler",
                            "itemOffered": {
                                "@type": "LoanOrCredit",
                                "name": "Bireysel İhtiyaç Kredisi",
                                "description": "İnternetten başvuru ile anında onaylanan bireysel ihtiyaç kredisi."
                            }
                        },
                        {
                            "@type": "Offer",
                            "url": "https://www.aktifbank.com.tr/n-kolay/krediler/taksitli-alisveris-kredisi",
                            "itemOffered": {
                                "@type": "LoanOrCredit",
                                "name": "Taksitli Alışveriş Kredisi",
                                "description": "Online ya da fiziksel mağaza alışverişlerinde kullanılabilen, vadeli ödeme kolaylığı sağlayan kredi çözümüdür."
                            }
                        },
                        {
                            "@type": "Offer",
                            "url": "https://www.aktifbank.com.tr/n-kolay/krediler/kolay-nakit",
                            "itemOffered": {
                                "@type": "LoanOrCredit",
                                "name": "Kolay Nakit",
                                "description": "N Kolay Kart sahiplerine özel, acil nakit ihtiyaçlarına hızlı çözüm sunan kredi hizmetidir."
                            }
                        },
                        {
                            "@type": "Offer",
                            "url": "https://www.aktifbank.com.tr/n-kolay/krediler/tasit-kredisi",
                            "itemOffered": {
                                "@type": "LoanOrCredit",
                                "name": "Taşıt Kredisi",
                                "description": "Yeni veya ikinci el araç alımlarında uygun faizli ve vadeli taşıt kredisi imkanı."
                            }
                        },
                        {
                            "@type": "Offer",
                            "url": "https://www.aktifbank.com.tr/n-kolay/krediler/kredili-destek-hesabi",
                            "itemOffered": {
                                "@type": "LoanOrCredit",
                                "name": "Kredili Destek Hesabı",
                                "description": "Vadesiz hesabınıza bağlı olarak tanımlanan ve nakit ihtiyacınızı anında karşılayan kredi limitidir."
                            }
                        }
                    ]
                }
            });
        }
    }

    addAboutPageSchema(): void {
        if (this.router.url === '/hakkimizda/bizi-taniyin/aktifbank-hakkinda') {
            this.addSchema('AboutPage', {
                "@context": "https://schema.org",
                "@type": "AboutPage",
                "name": "Aktif Bank Hakkımızda",
                "url": "https://www.aktifbank.com.tr/",
                "mainEntity": {
                    "@type": "BankOrCreditUnion",
                    "name": "Aktif Bank",
                    "foundingDate": "1999",
                    "url": "https://www.aktifbank.com.tr/",
                    "logo": "https://www.aktifbank.com.tr/assets/images/svg/logo.svg",
                    "description": "Türkiye'nin en büyük özel sermayeli yatırım bankası Aktif Bank, yatırım bankacılığını yeniden yorumladığı inovatif iş modelleri ve teknoloji yatırımları ile Türkiye'nin en kapsamlı finansal teknolojiler ekosistemi olarak faaliyetlerini sürdürüyor.",
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "Esentepe Mahallesi Kore Şehitleri Caddesi No: 8/1",
                        "addressLocality": "Şişli",
                        "postalCode": "34394",
                        "addressRegion": "İstanbul",
                        "addressCountry": "TR"
                    },
                    "telephone": "+90 212 340 80 00",
                    "sameAs": [
                        "https://www.instagram.com/explore/locations/360263589/aktifbank/",
                        "https://www.facebook.com/profile.php?id=1495018670720528&_rdr",
                        "https://www.aktifbank.com.tr/"
                    ],
                    "founder": {
                        "@type": "Person",
                        "name": "Ahmet Çalık"
                    },
                    "contactPoint": [
                        {
                            "@type": "ContactPoint",
                            "contactType": "customer service",
                            "telephone": "+90 212 340 80 00",
                            "availableLanguage": "TR"
                        }
                    ]
                }
            });
        }
    }

    resetSchemas(): void {
        this.schemaTypes.clear();
    }
} 